import React, { useEffect, useState } from 'react';
import { Users, Shield, Flame, Compass, AlertCircle, TrendingUp, Lightbulb, ArrowRight } from 'lucide-react';
import PIELayout from './PIELayout';
import { PIEPageSkeleton } from './PIESkeleton';
import { pieApi } from './pieApi';
import { pct } from './format';
import { Perf } from './PIEDisplayMode';
import PIETeamHeatmapCard from './PIETeamHeatmapCard';
import './pie.css';
import logger from '../../lib/logger';

/**
 * PIETeamPage — the manager view of the Personal Impact Engine.
 *
 * Privacy is the whole product here. The page is built to render
 * patterns, never people. If the backend says aggregates are
 * suppressed (k-anonymity floor not met, or too few opted-in) we
 * surface an explanatory empty state instead of partial data — the
 * suppression is the design, not a bug.
 */
export default function PIETeamPage() {
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [suggestions, setSuggestions] = useState(null); // null = loading; [] = none
  const [suggestionsErr, setSuggestionsErr] = useState(null);

  useEffect(() => {
    pieApi
      .teamSnapshot()
      .then((d) => {
        setSnapshot(d);
        // Kick off the AI co-pilot only when aggregates are visible.
        if (d?.aggregates_visible) {
          pieApi
            .teamSuggestions()
            .then((s) => setSuggestions(s.suggestions || []))
            .catch((e) => {
              logger.warn('team suggestions failed', e);
              setSuggestionsErr(true);
              setSuggestions([]);
            });
        } else {
          setSuggestions([]);
        }
      })
      .catch((e) => {
        logger.error('team snapshot failed', e);
        setErr(e?.response?.data?.detail || 'Could not load your team view.');
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <PIELayout>
        <PIEPageSkeleton cards={4} />
      </PIELayout>
    );
  }
  if (err) {
    return (
      <PIELayout>
        <div className="pie-error" data-testid="pie-team-error">{err}</div>
      </PIELayout>
    );
  }
  if (!snapshot?.is_manager && (snapshot?.team_size ?? 0) === 0) {
    return (
      <PIELayout>
        <NotAManagerState />
      </PIELayout>
    );
  }

  return (
    <PIELayout>
      <div className="pie-canvas" data-testid="pie-team-page">
        <section className="pie-hero-center" style={{ marginBottom: 28 }}>
          <span className="pie-eyebrow"><Users size={12} /> My team</span>
          <h1 className="pie-hero-h1" style={{ marginBottom: 12 }}>
            Patterns, not <span className="pie-accent-text">people</span>.
          </h1>
          <p className="pie-hero-sub">
            You see how your team is doing in aggregate — never an individual&apos;s reflection text. The
            people who reflect honestly stay protected; you see where coaching attention would land.
          </p>
        </section>

        <PrivacyBanner snapshot={snapshot} />

        {snapshot.aggregates_visible ? (
          <>
            <PEDistributionCard pe={snapshot.pe_distribution} teamSize={snapshot.consent_visible} />
            <PIETeamHeatmapCard />
            <StreakSummaryCard summary={snapshot.streak_summary} teamSize={snapshot.team_size} />
            <ThemesCard themes={snapshot.themes} themeMin={snapshot.theme_min} />
            <KpiHeadroomCard kpi={snapshot.kpi_headroom} />
            <SuggestionsCard suggestions={suggestions} errored={suggestionsErr} />
          </>
        ) : (
          <SuppressedState snapshot={snapshot} />
        )}
      </div>
    </PIELayout>
  );
}

function PrivacyBanner({ snapshot }) {
  return (
    <section className="pie-team-privacy-banner" data-testid="pie-team-privacy-banner">
      <Shield size={16} aria-hidden />
      <div>
        <div className="pie-team-privacy-headline">Privacy is the design, not a feature.</div>
        <div className="pie-team-privacy-sub">
          You see <strong>counts and themes</strong> only — never any individual&apos;s reflection text. Every
          aggregate needs at least <strong>{snapshot.k_threshold} reports</strong> to display, and a theme
          needs to be raised by at least <strong>{snapshot.theme_min} different reports</strong> before it
          shows up here. Your reports&apos; honest words to their Coach stay between them and the Coach.
        </div>
      </div>
    </section>
  );
}

function PEDistributionCard({ pe, teamSize }) {
  if (!pe) return null;
  const total = pe.buckets.reduce((s, b) => s + b.count, 0) || 1;
  return (
    <section className="pie-card" style={{ marginBottom: 20 }} data-testid="pie-team-pe-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
        <h2 className="pie-display-md">Effectiveness across the team</h2>
        <span className="pie-mono" style={{ fontSize: 14, color: 'var(--pie-text-secondary)' }}>
          Avg PE <strong><Perf value={pe.average_pe} kind="pct01" /></strong> · {teamSize} reports
        </span>
      </div>
      <p style={{ fontSize: 13, color: 'var(--pie-text-secondary)', marginBottom: 18, lineHeight: 1.55 }}>
        How many reports sit in each Personal Effectiveness band. The bigger the &ldquo;Constrained&rdquo; or
        &ldquo;Critical&rdquo; bars, the more opportunity there is for a system-level intervention vs individual coaching.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {pe.buckets.map((b) => {
          const pctOfTeam = Math.round((b.count / total) * 100);
          return (
            <div key={b.key} className={`pie-team-pe-row pie-team-pe-row-${b.key}`} data-testid={`pie-team-pe-${b.key}`}>
              <div className="pie-team-pe-row-label">{b.label}</div>
              <div className="pie-team-pe-row-bar" aria-hidden>
                <div
                  className={`pie-team-pe-row-fill pie-team-pe-row-fill-${b.key}`}
                  style={{ width: `${(b.count / total) * 100}%` }}
                />
              </div>
              <div className="pie-mono pie-team-pe-row-count">
                {b.count} <span className="pie-team-pe-row-pct">({pctOfTeam}%)</span>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function StreakSummaryCard({ summary, teamSize }) {
  if (!summary) return null;
  const reflectingCount = summary.active_streaks;
  const lapsedCount = summary.lapsed;
  const neverStarted = summary.never_started;
  return (
    <section className="pie-card" style={{ marginBottom: 20 }} data-testid="pie-team-streak-card">
      <h2 className="pie-display-md" style={{ marginBottom: 8 }}>Reflection rhythm</h2>
      <p style={{ fontSize: 13, color: 'var(--pie-text-secondary)', marginBottom: 18, lineHeight: 1.55 }}>
        Wrap-ups in the last 14 days mean a report is actively engaged in the loop — capable of being
        coached on what worked and what didn&apos;t. Lapsed and never-started reports are a hint to lower
        the activation barrier (lighter actions, clearer first steps), not to push harder.
      </p>
      <div className="pie-team-streak-row">
        <StreakStat
          icon={<Flame size={18} />}
          label="Actively reflecting"
          value={reflectingCount}
          total={teamSize}
          tint="active"
          testid="pie-team-streak-active"
        />
        <StreakStat
          icon={<AlertCircle size={18} />}
          label="Lapsed (>14 days)"
          value={lapsedCount}
          total={teamSize}
          tint="warn"
          testid="pie-team-streak-lapsed"
        />
        <StreakStat
          icon={<Compass size={18} />}
          label="Never started"
          value={neverStarted}
          total={teamSize}
          tint="quiet"
          testid="pie-team-streak-never"
        />
      </div>
    </section>
  );
}

function StreakStat({ icon, label, value, total, tint, testid }) {
  const pctOfTeam = total ? Math.round((value / total) * 100) : 0;
  return (
    <div className={`pie-team-streak-stat pie-team-streak-stat-${tint}`} data-testid={testid}>
      <div className="pie-team-streak-icon">{icon}</div>
      <div className="pie-team-streak-text">
        <div className="pie-team-streak-value">{value}</div>
        <div className="pie-team-streak-label">{label}</div>
        <div className="pie-team-streak-pct">{pctOfTeam}% of team</div>
      </div>
    </div>
  );
}

function ThemesCard({ themes, themeMin }) {
  return (
    <section className="pie-card" style={{ marginBottom: 20 }} data-testid="pie-team-themes-card">
      <h2 className="pie-display-md" style={{ marginBottom: 8 }}>What&apos;s sticking. What isn&apos;t.</h2>
      <p style={{ fontSize: 13, color: 'var(--pie-text-secondary)', marginBottom: 18, lineHeight: 1.55 }}>
        Themes pulled from your team&apos;s wrap-ups by an AI classifier — bucketed into a fixed set
        so no individual reflection ever leaks. Each theme below was raised by at least{' '}
        <strong>{themeMin} different reports</strong>. The numbers are how many distinct reports
        mentioned each theme.
      </p>
      {(!themes || themes.length === 0) ? (
        <div style={{ fontSize: 13, color: 'var(--pie-text-muted)', fontStyle: 'italic', padding: '12px 0' }}>
          No themes have crossed the privacy threshold yet — your team needs a few more wrap-ups.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {themes.map((t) => (
            <div key={t.key} className="pie-team-theme-row" data-testid={`pie-team-theme-${t.key}`}>
              <span className="pie-team-theme-label">{t.label}</span>
              <span className="pie-team-theme-count pie-mono">
                {t.count} <span className="pie-team-theme-count-suffix">reports</span>
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function KpiHeadroomCard({ kpi }) {
  if (!kpi) return null;
  return (
    <section className="pie-card" style={{ marginBottom: 20 }} data-testid="pie-team-kpi-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
        <h2 className="pie-display-md">Aggregate headroom</h2>
        <span className="pie-mono pie-team-kpi-total">
          <TrendingUp size={14} aria-hidden style={{ verticalAlign: -2, marginRight: 4 }} />
          ${(kpi.total_yr || 0).toLocaleString()} / yr
        </span>
      </div>
      <p style={{ fontSize: 13, color: 'var(--pie-text-secondary)', marginBottom: 18, lineHeight: 1.55 }}>
        Total annual dollar value your team would unlock by closing the gap to 100% Personal Effectiveness
        across every reporting persona — by KPI. This is the size of the prize for system-level coaching.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {(kpi.rows || []).map((row) => (
          <div key={row.kpi_id} className="pie-team-kpi-row" data-testid={`pie-team-kpi-${row.kpi_id}`}>
            <span className="pie-team-kpi-row-name">↑ {row.name}</span>
            <span className="pie-mono pie-team-kpi-row-amount">${row.headroom_total_yr.toLocaleString()} /yr</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function SuggestionsCard({ suggestions, errored }) {
  // Loading state — null while the LLM call is in flight.
  if (suggestions === null) {
    return (
      <section className="pie-card pie-team-suggestions-card pie-team-suggestions-card-loading" data-testid="pie-team-suggestions-loading">
        <div className="pie-team-suggestions-head">
          <Lightbulb size={16} aria-hidden />
          <h2 className="pie-display-md">What I&apos;d suggest, as a manager</h2>
        </div>
        <p className="pie-team-suggestions-foot">
          Reading the aggregate signals above and proposing 2–3 specific moves you could run this week…
        </p>
      </section>
    );
  }
  if (suggestions.length === 0) {
    if (errored) {
      return (
        <section className="pie-card pie-team-suggestions-card" data-testid="pie-team-suggestions-error">
          <div className="pie-team-suggestions-head">
            <Lightbulb size={16} aria-hidden />
            <h2 className="pie-display-md">What I&apos;d suggest, as a manager</h2>
          </div>
          <p className="pie-team-suggestions-foot" style={{ color: 'var(--pie-text-muted)' }}>
            The AI co-pilot couldn&apos;t generate suggestions this round. Refresh in a moment, or use the patterns above directly.
          </p>
        </section>
      );
    }
    return null;
  }
  return (
    <section className="pie-card pie-team-suggestions-card" data-testid="pie-team-suggestions-card">
      <div className="pie-team-suggestions-head">
        <Lightbulb size={16} aria-hidden />
        <h2 className="pie-display-md">What I&apos;d suggest, as a manager</h2>
      </div>
      <p className="pie-team-suggestions-intro">
        Generated from the privacy-protected aggregate above — PE distribution + theme counts +
        headroom. <strong>No reflection text</strong> ever enters this prompt.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {suggestions.map((s, i) => (
          <div key={i} className="pie-team-suggestion" data-testid={`pie-team-suggestion-${i}`}>
            <div className="pie-team-suggestion-num pie-mono">{i + 1}</div>
            <div className="pie-team-suggestion-body">
              <h3 className="pie-team-suggestion-title">{s.title}</h3>
              {s.rationale && (
                <p className="pie-team-suggestion-rationale">{s.rationale}</p>
              )}
              {s.expected_value && (
                <div className="pie-team-suggestion-expected">
                  <TrendingUp size={11} aria-hidden /> {s.expected_value}
                </div>
              )}
              {s.first_step && (
                <div className="pie-team-suggestion-step">
                  <ArrowRight size={11} aria-hidden /> <strong>Start here:</strong> {s.first_step}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      <p className="pie-team-suggestions-foot">
        Suggestions are advisory. They&apos;re generated fresh each time you load this page from the same aggregate the cards above show.
      </p>
    </section>
  );
}

function SuppressedState({ snapshot }) {
  const reason = snapshot.suppression_reason;
  const headline = reason === 'too_few_opted_in'
    ? 'Too few reports have opted-in to the manager view.'
    : `You need at least ${snapshot.k_threshold} reports for any aggregate to show.`;
  return (
    <section className="pie-card" style={{ textAlign: 'center', padding: '40px 24px' }} data-testid="pie-team-suppressed">
      <Shield size={28} style={{ color: 'var(--pie-accent)', marginBottom: 12 }} aria-hidden />
      <h2 className="pie-display-md" style={{ marginBottom: 8 }}>{headline}</h2>
      <p style={{ fontSize: 14, color: 'var(--pie-text-secondary)', maxWidth: 480, margin: '0 auto', lineHeight: 1.6 }}>
        Your team currently has <strong>{snapshot.team_size}</strong> reports
        {snapshot.consent_visible !== undefined && (
          <> · <strong>{snapshot.consent_visible}</strong> opted-in</>
        )}.
        We never display partial data because it&apos;s too easy to identify a single person from a tiny aggregate.
        Once the threshold is met, your team view will fill in automatically.
      </p>
    </section>
  );
}

function NotAManagerState() {
  return (
    <div className="pie-canvas">
      <section className="pie-card" style={{ textAlign: 'center', padding: '60px 24px' }} data-testid="pie-team-not-manager">
        <Users size={28} style={{ color: 'var(--pie-accent)', marginBottom: 12 }} aria-hidden />
        <h2 className="pie-display-md" style={{ marginBottom: 8 }}>You don&apos;t have any direct reports yet.</h2>
        <p style={{ fontSize: 14, color: 'var(--pie-text-secondary)', maxWidth: 480, margin: '0 auto', lineHeight: 1.6 }}>
          The &ldquo;My team&rdquo; view shows aggregate patterns across your direct reports
          (PE distribution, reflection rhythm, themes raised) without ever showing any individual&apos;s
          reflection text. When reports are assigned to your persona, this page will fill in.
        </p>
      </section>
    </div>
  );
}
